// v10
// Path: frontend/src/components/SyncHub/EditConnectionGroupModal.jsx
// Inputs: token, group, dbProfiles = [], accounts = [], onClose, onSaved
// Outputs: React JSX modal allowing creation and editing of connection groups with independent company filtering, explicit selections, dual container reachability test, and auto-generated group IDs
// Summary: Connection group editor supporting auto-generated ConnectionGroupID identifiers, explicit user container selection, company/organization filters per side, dual container reachability diagnostics, and organization assignment.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import SearchableSelect from './SearchableSelect';
import { useSyncStore } from './useSyncStore';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const EditConnectionGroupModal = ({
    token,
    group,
    dbProfiles = [],
    accounts = [],
    onClose,
    onSaved
}) => {
    const isNew = !group || group.isNew === true || (!group.connectionGroupId && !group.ConnectionGroupID);
    const [saving, setSaving] = useState(false);

    // Containers & Organizations from Data Catalog Studio
    const [catalogConnections, setCatalogConnections] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    // Selected Container IDs (strict explicit selection - no auto defaults)
    const [selectedSourceContainerId, setSelectedSourceContainerId] = useState(
        group?.sourceContainerId ? String(group.sourceContainerId) : (group?.SourceContainerID ? String(group.SourceContainerID) : '')
    );
    const [selectedTargetContainerId, setSelectedTargetContainerId] = useState(
        group?.targetContainerId ? String(group.targetContainerId) : (group?.TargetContainerID ? String(group.TargetContainerID) : '')
    );

    // Independent Company/Organization filter dropdowns for Source & Target
    const [sourceOrgFilter, setSourceOrgFilter] = useState('');
    const [targetOrgFilter, setTargetOrgFilter] = useState('');

    // Group Company Assignment
    const [groupOrganizationId, setGroupOrganizationId] = useState(
        group?.organizationId ? String(group.organizationId) : (group?.OrganizationID ? String(group.OrganizationID) : '')
    );

    // Group Metadata State
    const [groupName, setGroupName] = useState(group?.groupName || group?.GroupName || '');
    const [connectionGroupId, setConnectionGroupId] = useState(group?.connectionGroupId || group?.ConnectionGroupID || '');
    const [sourceSystem, setSourceSystem] = useState(group?.sourceSystem || group?.SourceSystem || '');
    const [targetSystem, setTargetSystem] = useState(group?.targetSystem || group?.TargetSystem || '');
    const [createDefaultPipelines, setCreateDefaultPipelines] = useState(true);

    // Diagnostics / Dual Reachability Testing
    const [testingSource, setTestingSource] = useState(false);
    const [testingTarget, setTestingTarget] = useState(false);
    const [sourceTestResult, setSourceTestResult] = useState(null);
    const [targetTestResult, setTargetTestResult] = useState(null);

    // ── 1. Load Data Catalog Studio Connections & Organizations ──────────────────
    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            setLoadingData(true);
            try {
                const [connsRes, orgsRes] = await Promise.all([
                    fetch('/api/catalog/connections', { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch('/api/catalog/organizations', { headers: { 'Authorization': `Bearer ${token}` } })
                ]);

                if (connsRes.ok) {
                    const cData = await unbox(connsRes);
                    const conns = Array.isArray(cData) ? cData : (cData.data || []);
                    if (isMounted) setCatalogConnections(conns);
                }

                if (orgsRes.ok) {
                    const oData = await unbox(orgsRes);
                    const orgs = Array.isArray(oData) ? oData : (oData.data || []);
                    if (isMounted) setOrganizations(orgs);
                }
            } catch (err) {
                console.warn('[EditConnectionGroupModal] Error loading catalog data:', err);
            } finally {
                if (isMounted) setLoadingData(false);
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, [token]);

    // Keep name in sync if editing an existing group
    useEffect(() => {
        if (!isNew && group) {
            if (group.groupName || group.GroupName) {
                setGroupName(group.groupName || group.GroupName);
            }
            if (group.connectionGroupId || group.ConnectionGroupID) {
                setConnectionGroupId(group.connectionGroupId || group.ConnectionGroupID);
            }
            if (group.sourceContainerId || group.SourceContainerID) {
                setSelectedSourceContainerId(String(group.sourceContainerId || group.SourceContainerID));
            }
            if (group.targetContainerId || group.TargetContainerID) {
                setSelectedTargetContainerId(String(group.targetContainerId || group.TargetContainerID));
            }
            if (group.organizationId || group.OrganizationID) {
                setGroupOrganizationId(String(group.organizationId || group.OrganizationID));
            }
        }
    }, [group, isNew]);

    // Keyboard dismiss on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Deduplicate unique containers by ContainerID
    const uniqueContainers = useMemo(() => {
        const map = new Map();
        (catalogConnections || []).forEach(c => {
            if (!c.ContainerID) return;
            if (!map.has(c.ContainerID) || (!map.get(c.ContainerID).CredentialID && c.CredentialID)) {
                map.set(c.ContainerID, c);
            }
        });
        return Array.from(map.values());
    }, [catalogConnections]);

    const sourceContainer = useMemo(() => {
        return uniqueContainers.find(c => String(c.ContainerID) === String(selectedSourceContainerId)) || null;
    }, [uniqueContainers, selectedSourceContainerId]);

    const targetContainer = useMemo(() => {
        return uniqueContainers.find(c => String(c.ContainerID) === String(selectedTargetContainerId)) || null;
    }, [uniqueContainers, selectedTargetContainerId]);

    // Update derived system names and default org filters when containers are selected
    const prevSourceContainerIdRef = useRef(null);
    useEffect(() => {
        if (sourceContainer) {
            setSourceSystem(sourceContainer.SourceAppName || sourceContainer.SourceAppCode || 'Source');
            if (prevSourceContainerIdRef.current !== selectedSourceContainerId) {
                prevSourceContainerIdRef.current = selectedSourceContainerId;
                const orgId = sourceContainer.EffectiveOrganizationID || sourceContainer.ContainerOrgID || sourceContainer.InstanceOrgID || sourceContainer.HostOrgID;
                if (orgId) {
                    setSourceOrgFilter(String(orgId));
                }
            }
        }
    }, [sourceContainer, selectedSourceContainerId]);

    const prevTargetContainerIdRef = useRef(null);
    useEffect(() => {
        if (targetContainer) {
            setTargetSystem(targetContainer.SourceAppName || targetContainer.SourceAppCode || 'Target');
            if (prevTargetContainerIdRef.current !== selectedTargetContainerId) {
                prevTargetContainerIdRef.current = selectedTargetContainerId;
                const orgId = targetContainer.EffectiveOrganizationID || targetContainer.ContainerOrgID || targetContainer.InstanceOrgID || targetContainer.HostOrgID;
                if (orgId) {
                    setTargetOrgFilter(String(orgId));
                }
            }
        }
    }, [targetContainer, selectedTargetContainerId]);

    // Filter available source containers based on independent sourceOrgFilter
    const availableSourceContainers = useMemo(() => {
        if (!sourceOrgFilter) return uniqueContainers;
        return uniqueContainers.filter(c => {
            const orgId = c.EffectiveOrganizationID || c.ContainerOrgID || c.InstanceOrgID || c.HostOrgID;
            return String(orgId) === String(sourceOrgFilter) || String(c.OrganizationName) === String(sourceOrgFilter);
        });
    }, [uniqueContainers, sourceOrgFilter]);

    // Filter available target containers based on independent targetOrgFilter
    const availableTargetContainers = useMemo(() => {
        if (!targetOrgFilter) return uniqueContainers;
        return uniqueContainers.filter(c => {
            const orgId = c.EffectiveOrganizationID || c.ContainerOrgID || c.InstanceOrgID || c.HostOrgID;
            return String(orgId) === String(targetOrgFilter) || String(c.OrganizationName) === String(targetOrgFilter);
        });
    }, [uniqueContainers, targetOrgFilter]);

    // Format rich descriptive labels so users can easily discern between containers with the same database name
    const formatContainerOption = (c) => {
        const credPart = c.CredentialName ? ` [${c.CredentialName}]` : '';
        const hostPart = c.HostDisplayName || c.HostName || c.Hostname || 'Endpoint';
        const instPart = c.InstanceName ? ` on ${c.InstanceName}` : '';
        const orgPart = c.OrganizationName || 'Unassigned Org';
        const appPart = c.SourceAppName || c.SourceAppCode || 'App';

        return {
            value: String(c.ContainerID),
            label: `${c.ContainerName}${credPart}`,
            sublabel: `${orgPart} • ${appPart} • ${hostPart}${instPart}`,
            description: `${c.ContainerType || 'DATABASE'} | Port: ${c.ListenPort || 1433}`,
            isPowered: !!(c.isConfigured || c.CredentialStatus === 'CONFIGURED' || c.CredentialID)
        };
    };

    // Test Reachability for BOTH Source and Target Containers
    const handleTestBothContainers = async () => {
        if (!sourceContainer && !targetContainer) {
            useSyncStore.getState().showAlert('Please select at least one container to test.');
            return;
        }

        if (sourceContainer) {
            setTestingSource(true);
            setSourceTestResult(null);
            try {
                const res = await fetch(`/api/catalog/containers/${sourceContainer.ContainerID}/test`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data = await unbox(res);
                setSourceTestResult(data);
            } catch (err) {
                setSourceTestResult({ success: false, error: err.message });
            } finally {
                setTestingSource(false);
            }
        }

        if (targetContainer) {
            setTestingTarget(true);
            setTargetTestResult(null);
            try {
                const res = await fetch(`/api/catalog/containers/${targetContainer.ContainerID}/test`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data = await unbox(res);
                setTargetTestResult(data);
            } catch (err) {
                setTargetTestResult({ success: false, error: err.message });
            } finally {
                setTestingTarget(false);
            }
        }
    };

    // ── Save Connection Group ───────────────────────────────────────────────────
    const handleSaveGroup = async (e) => {
        e.preventDefault();
        if (!groupName.trim()) {
            useSyncStore.getState().showAlert('Please provide a Group Display Name.');
            return;
        }
        if (!selectedSourceContainerId || !sourceContainer) {
            useSyncStore.getState().showAlert('Please select a Source Connection / Container.');
            return;
        }
        if (!selectedTargetContainerId || !targetContainer) {
            useSyncStore.getState().showAlert('Please select a Target Connection / Container.');
            return;
        }

        const effectiveDb = sourceContainer.ContainerName;
        const effectiveAccountId = targetContainer.CredentialID || 1;
        const hostDisp = sourceContainer.HostDisplayName || sourceContainer.HostName || 'LocalServer';
        const hostIp = sourceContainer.HostName || 'localhost';
        const svc = sourceContainer.InstanceName || 'ssinstance';
        const port = sourceContainer.ListenPort || 1433;
        const effectivePrefix = `[${hostDisp}|${hostIp}|${svc}|${port}|sa]`;

        setSaving(true);
        try {
            let res;
            if (isNew) {
                res = await fetch('/api/sync/connection-groups', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        connectionGroupId: connectionGroupId.trim() || undefined,
                        groupName: groupName.trim(),
                        organizationId: groupOrganizationId ? parseInt(groupOrganizationId, 10) : (sourceContainer.EffectiveOrganizationID || null),
                        accountId: effectiveAccountId,
                        sourceContainerId: parseInt(selectedSourceContainerId, 10),
                        targetContainerId: parseInt(selectedTargetContainerId, 10),
                        sourceConnPrefix: effectivePrefix,
                        sourceDatabaseName: effectiveDb,
                        sourceSystem: sourceSystem || sourceContainer.SourceAppName || 'Source',
                        targetSystem: targetSystem || targetContainer.SourceAppName || 'Target',
                        createDefaultPipelines
                    })
                });
            } else {
                const targetGid = group?.connectionGroupId || group?.ConnectionGroupID || connectionGroupId;
                res = await fetch(`/api/sync/connection-groups/${encodeURIComponent(targetGid)}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        groupName: groupName.trim(),
                        organizationId: groupOrganizationId ? parseInt(groupOrganizationId, 10) : null,
                        accountId: effectiveAccountId,
                        sourceContainerId: parseInt(selectedSourceContainerId, 10),
                        targetContainerId: parseInt(selectedTargetContainerId, 10),
                        sourceConnPrefix: effectivePrefix,
                        sourceDatabaseName: effectiveDb,
                        sourceSystem: sourceSystem || sourceContainer.SourceAppName,
                        targetSystem: targetSystem || targetContainer.SourceAppName
                    })
                });
            }

            const data = await unbox(res);
            if (!res.ok) {
                throw new Error(data.error || 'Failed to save connection group.');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            useSyncStore.getState().showAlert(`Error saving connection group: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div 
            onClick={onClose}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto"
        >
            <div 
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-850 border border-slate-700 rounded-2xl max-w-3xl w-full p-6 shadow-2xl space-y-5 my-8"
            >
                {/* Header */}
                <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                            <span className="material-symbols-outlined text-[18px]">hub</span>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white">
                                {isNew ? 'Create New Connection Group' : `Edit Connection Group: ${groupName || connectionGroupId}`}
                            </h3>
                            <p className="text-[11px] text-slate-400">
                                Define an explicit synchronization pair between two cataloged data containers.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" title="Close Modal">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <form onSubmit={handleSaveGroup} className="space-y-4">
                    {/* Top Row: Group Display Name & Assigned Organization */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Group Display Name <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                placeholder="Enter a descriptive name (e.g. Abbott Ferry ➔ Production Accounting)"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Assign to Company / Organization
                            </label>
                            <SearchableSelect
                                disabled={loadingData}
                                placeholder="-- Select Company (Optional) --"
                                value={groupOrganizationId}
                                onChange={(val) => setGroupOrganizationId(val)}
                                options={organizations.map(org => ({
                                    value: String(org.OrganizationID),
                                    label: org.OrganizationName,
                                    sublabel: org.OrganizationType || 'Organization'
                                }))}
                            />
                        </div>
                    </div>

                    {/* Source & Target Container Selection with Independent Organization Filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
                        {/* Source Side Container */}
                        <div className="p-3.5 bg-slate-900/60 border border-slate-800 rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-sky-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[15px]">input</span>
                                    Source Connection
                                </span>
                                {sourceContainer && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-950 text-sky-300 border border-sky-800 font-mono">
                                        ID #{sourceContainer.ContainerID}
                                    </span>
                                )}
                            </div>

                            {/* Independent Organization Filter for Source */}
                            <div>
                                <label className="block text-[11px] font-medium text-slate-400 mb-1">
                                    Filter by Source Company / Org:
                                </label>
                                <SearchableSelect
                                    disabled={loadingData}
                                    placeholder="All Companies / Orgs"
                                    value={sourceOrgFilter}
                                    onChange={(val) => setSourceOrgFilter(val)}
                                    options={[
                                        { value: '', label: 'All Companies / Organizations' },
                                        ...organizations.map(org => ({
                                            value: String(org.OrganizationID),
                                            label: org.OrganizationName,
                                            sublabel: org.OrganizationType || 'Organization'
                                        }))
                                    ]}
                                />
                            </div>

                            {/* Source Container Dropdown */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">
                                    Select Source Container <span className="text-red-400">*</span>
                                </label>
                                <SearchableSelect
                                    disabled={loadingData}
                                    placeholder={loadingData ? 'Loading containers...' : '-- Choose Source Container --'}
                                    value={selectedSourceContainerId}
                                    onChange={(val) => setSelectedSourceContainerId(val)}
                                    options={availableSourceContainers.map(formatContainerOption)}
                                />
                            </div>

                            {sourceContainer && (
                                <div className="p-2 bg-slate-950/80 rounded border border-slate-800 text-[11px] text-slate-300 space-y-0.5">
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Application:</span>
                                        <strong className="text-sky-300 font-mono">{sourceContainer.SourceAppName || sourceContainer.SourceAppCode}</strong>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Host / Instance:</span>
                                        <span className="text-slate-200 font-mono">{sourceContainer.HostDisplayName} ({sourceContainer.InstanceName})</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Bound Credential:</span>
                                        <span className="text-emerald-300 font-mono">{sourceContainer.CredentialName || 'None'}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Target Side Container */}
                        <div className="p-3.5 bg-slate-900/60 border border-slate-800 rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[15px]">output</span>
                                    Target Connection
                                </span>
                                {targetContainer && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-950 text-emerald-300 border border-emerald-800 font-mono">
                                        ID #{targetContainer.ContainerID}
                                    </span>
                                )}
                            </div>

                            {/* Independent Organization Filter for Target */}
                            <div>
                                <label className="block text-[11px] font-medium text-slate-400 mb-1">
                                    Filter by Target Company / Org:
                                </label>
                                <SearchableSelect
                                    disabled={loadingData}
                                    placeholder="All Companies / Orgs"
                                    value={targetOrgFilter}
                                    onChange={(val) => setTargetOrgFilter(val)}
                                    options={[
                                        { value: '', label: 'All Companies / Organizations' },
                                        ...organizations.map(org => ({
                                            value: String(org.OrganizationID),
                                            label: org.OrganizationName,
                                            sublabel: org.OrganizationType || 'Organization'
                                        }))
                                    ]}
                                />
                            </div>

                            {/* Target Container Dropdown */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">
                                    Select Target Container <span className="text-red-400">*</span>
                                </label>
                                <SearchableSelect
                                    disabled={loadingData}
                                    placeholder={loadingData ? 'Loading containers...' : '-- Choose Target Container --'}
                                    value={selectedTargetContainerId}
                                    onChange={(val) => setSelectedTargetContainerId(val)}
                                    options={availableTargetContainers.map(formatContainerOption)}
                                />
                            </div>

                            {targetContainer && (
                                <div className="p-2 bg-slate-950/80 rounded border border-slate-800 text-[11px] text-slate-300 space-y-0.5">
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Application:</span>
                                        <strong className="text-emerald-300 font-mono">{targetContainer.SourceAppName || targetContainer.SourceAppCode}</strong>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Tenant / Type:</span>
                                        <span className="text-slate-200 font-mono">{targetContainer.ContainerType}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Bound Credential:</span>
                                        <span className="text-emerald-300 font-mono">{targetContainer.CredentialName || 'None'}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Pre-population Options for New Group */}
                    {isNew && (
                        <div className="p-3 bg-slate-900 border border-slate-700/80 rounded-xl space-y-1.5">
                            <label className="flex items-start gap-2.5 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={createDefaultPipelines}
                                    onChange={(e) => setCreateDefaultPipelines(e.target.checked)}
                                    className="w-4 h-4 mt-0.5 rounded text-blue-600 focus:ring-0 focus:outline-none bg-slate-850 border-slate-700 cursor-pointer"
                                />
                                <div className="text-xs">
                                    <span className="font-bold text-white block">Initialize Pipelines from Catalog Presets</span>
                                    <span className="text-slate-400 block mt-0.5">
                                        Automatically creates initial entity pipelines (e.g. Customers, Items, Invoices) matching {sourceSystem || 'Source'} and {targetSystem || 'Target'}.
                                    </span>
                                </div>
                            </label>
                        </div>
                    )}

                    {/* Dual Reachability Test */}
                    {(sourceContainer || targetContainer) && (
                        <div className="p-3 bg-slate-900/80 border border-slate-800 rounded-xl space-y-2.5">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-200 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-blue-400">wifi_find</span>
                                    Connection Reachability Diagnostics
                                </span>
                                <button
                                    type="button"
                                    onClick={handleTestBothContainers}
                                    disabled={testingSource || testingTarget}
                                    className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                                    title="Verify connectivity to both source and target containers"
                                >
                                    <span className={`material-symbols-outlined text-[14px] ${(testingSource || testingTarget) ? 'animate-spin' : ''}`}>
                                        {(testingSource || testingTarget) ? 'sync' : 'wifi'}
                                    </span>
                                    {(testingSource || testingTarget) ? 'Testing Containers...' : 'Test Both Containers'}
                                </button>
                            </div>

                            {/* Results Panel */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                {sourceContainer && (
                                    <div className="p-2 rounded bg-slate-950 border border-slate-800">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-slate-400 font-mono text-[11px]">Source: {sourceContainer.ContainerName}</span>
                                            {testingSource && <span className="text-sky-400 animate-pulse text-[10px]">Testing...</span>}
                                        </div>
                                        {sourceTestResult && (
                                            <div className={`p-1.5 rounded font-mono text-[10px] ${sourceTestResult.success ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800' : 'bg-red-950/80 text-red-300 border border-red-800'}`}>
                                                {sourceTestResult.success ? '✓ Reachable & Connected' : (sourceTestResult.error || 'Connection Failed')}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {targetContainer && (
                                    <div className="p-2 rounded bg-slate-950 border border-slate-800">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-slate-400 font-mono text-[11px]">Target: {targetContainer.ContainerName}</span>
                                            {testingTarget && <span className="text-emerald-400 animate-pulse text-[10px]">Testing...</span>}
                                        </div>
                                        {targetTestResult && (
                                            <div className={`p-1.5 rounded font-mono text-[10px] ${targetTestResult.success ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800' : 'bg-red-950/80 text-red-300 border border-red-800'}`}>
                                                {targetTestResult.success ? '✓ Reachable & Connected' : (targetTestResult.error || 'Connection Failed')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all shadow-md flex items-center gap-1.5"
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                {saving ? 'sync' : 'save'}
                            </span>
                            {saving ? 'Saving...' : (isNew ? 'Create Connection Group' : 'Save Changes')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditConnectionGroupModal;
